import { Box, Button, ChakraProvider } from "@chakra-ui/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import theme from "../../theme"; // Adjust the path as necessary
import Sidebar from "../navigation/Sidebar";

import { Auth0Provider } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import { FeatureHintDialog } from "../feature-hints/FeatureHintDialog";
import { FeatureHintsProvider } from "../feature-hints/FeatureHintsContext";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { Context, ContextProvider } from "./context";
import ErrorPage from "./ErrorPage";

const AuthProvider = ({ children }: any) => {
  const redirectUri = window.location.origin;

  if (
    !(
      process.env.REACT_APP_AUTH0_DOMAIN &&
      process.env.REACT_APP_AUTH0_ID &&
      process.env.REACT_APP_AUTH0_AUDIENCE &&
      redirectUri
    )
  ) {
    return null;
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: "openid profile email offline_access",
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        ui_locales: "de",
      }}
    >
      {children}
    </Auth0Provider>
  );
};

const RootWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const excludeSidebar = [
    "/register",
    "/organization/neu",
    "/reset",
    "/verification/mail",
    "/verification/2fa",
    "/stellenangebote/iframe",
    "/job-angebote/iframe/",
    "/pflegeplatz-anfragen",
    "/new-onboarding",
    // Der Super-Admin-Bereich traegt seine eigene Navigation (KAN-276, A8). Die blaue
    // Leiste zeigte hier nur Kundenwege, die ein Super-Admin gar nicht gehen kann - plus
    // ein Zahnrad auf die Seite, auf der er schon steht. Waehrend einer Impersonierung
    // bleibt sie unberuehrt: dort laeuft er auf Kundenrouten, nicht auf /super-admin.
    "/super-admin",
  ];

  const excludeBackground = ["/stellenangebote/iframe", "/job-angebote/iframe/", "/pflegeplatz-anfragen"];

  const dontShowAdmin = ["/new-onboarding"];

  const location = useLocation();

  const includes = excludeSidebar.some((path) => location.pathname.includes(path));
  const includesBackground = excludeBackground.some((path) => location.pathname.includes(path));
  const includesDontShowAdmin = dontShowAdmin.some((path) => location.pathname.includes(path));
  return (
    <AuthProvider>
      <ChakraProvider theme={theme}>
        <Box display="flex" gap="1em" width="100%" height="100%" minHeight="100vh">
          <ContextProvider>
            {/* Seitenleiste und Dialog teilen sich einen Stand der Hinweise (KAN-251):
                Klickt jemand den Dialog weg, zaehlt das Abzeichen sofort herunter, statt
                bis zum naechsten Laden falsch dazustehen. */}
            <FeatureHintsProvider>
              {!includes && <Sidebar />}
              {/* Dieselbe Grenze wie die Seitenleiste: nur auf angemeldeten Seiten, nie auf dem
                  oeffentlichen Bewerbungs- oder Anfrageformular (KAN-241, A7). Waehrend einer
                  Super-Admin-Impersonierung liefert das Backend ohnehin nichts zurueck. */}
              {!includes && <FeatureHintDialog />}
            </FeatureHintsProvider>
            <Wrapper margin={!includes} background={includesBackground} showAdmin={!includesDontShowAdmin}>
              {children}
            </Wrapper>
          </ContextProvider>
        </Box>
      </ChakraProvider>
    </AuthProvider>
  );
};

const Wrapper = ({
  children,
  margin,
  background,
  showAdmin,
}: {
  children: React.ReactNode;
  margin: boolean;
  background: boolean;
  showAdmin: boolean;
}) => {
  const { user } = useUser();
  try {
    return (
      <>
        <Box
          className={margin ? "py-3 px-5 min-h-full overflow-hidden" : ""}
          marginLeft={margin ? "82px" : ""}
          // Platz nur fuer die Leiste, die wirklich erscheint: Inhalt traegt sie allein
          // waehrend einer Impersonierung. Sonst blieb hier ein leerer Streifen stehen.
          paddingBottom={user?.showBackToSuperadmin ? "4rem" : ""}
          background={background ? "transparent" : "#F9FAFC"}
          minHeight="100vh"
          width="100%"
        >
          {children}
        </Box>
        {(user?.showBackToSuperadmin || user?.showSuperadminPanel) && showAdmin && <SuperAdminWrapper />}
      </>
    );
  } catch (error) {
    return <ErrorPage />;
  }
};

/** How often the bar checks whether it still shows the organization we actually act as. */
const CONTEXT_CHECK_INTERVAL_MS = 30000;

/**
 * Watches whether the organization behind this session is still the one the page was
 * loaded with.
 *
 * Which organization a super admin acts as lives in a single field on their user
 * document, shared by every session of that user - a second tab, another device, an MCP
 * client. Any of them can switch it, and this page would not notice: it learns the
 * organization once, when it loads. That is how a customer's pixel ended up on our own
 * organization, saved through a page that still showed the customer.
 *
 * Polling is enough here. The window is a person filling in a form, not milliseconds,
 * and the check also runs whenever the tab regains focus - the moment someone returns
 * from whatever switched the context.
 */
const useOrganizationContextWatch = (isActive: boolean, currentOrganizationId?: string) => {
  const { accessToken } = useContext(Context);
  const [liveOrganizationName, setLiveOrganizationName] = useState<string>();

  const check = useCallback(async () => {
    if (!isActive || !currentOrganizationId || !accessToken) return;

    // Deliberately a bare fetch instead of useApiRequest: that one raises the global
    // error modal on any failure, and a check running every 30 seconds must never put a
    // dialog in front of someone over a single hiccup. A failed check simply changes
    // nothing and the next one tries again.
    try {
      const base = process.env.REACT_APP_API?.includes("localhost")
        ? `${process.env.REACT_APP_API}/v1.0`
        : `${process.env.REACT_APP_API}/api/v1.0`;

      const response = await fetch(`${base}/users/userinfo`, { headers: { authorization: `Bearer ${accessToken}` } });
      if (!response.ok) return;

      const userinfo = (await response.json()) as { organizationId?: string; organizationName?: string };
      if (!userinfo.organizationId) return;

      setLiveOrganizationName(
        userinfo.organizationId === currentOrganizationId ? undefined : userinfo.organizationName ?? "einer anderen Organisation",
      );
    } catch {
      // Offline or a dropped connection - not something to report here.
    }
  }, [isActive, currentOrganizationId, accessToken]);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(check, CONTEXT_CHECK_INTERVAL_MS);
    window.addEventListener("focus", check);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", check);
    };
  }, [check, isActive]);

  return liveOrganizationName;
};

const SuperAdminWrapper = () => {
  const { user } = useUser();
  const { apiRequest } = useApiRequest();
  const changedToOrganizationName = useOrganizationContextWatch(Boolean(user?.showBackToSuperadmin), user?.organizationId);

  const logout = async () => {
    await apiRequest("organizations/logout", "POST");
    window.location.href = "/super-admin";
  };

  return (
    <div className="fixed bottom-0 m-auto flex justify-center w-1/2 left-0 right-0 z-50">
      {user?.showBackToSuperadmin ? (
        <div
          className={`border-2 border-b-0 w-full p-4 ${
            changedToOrganizationName ? "border-red-600 bg-red-50" : "border-primary bg-white"
          }`}
        >
          <div className="flex justify-between">
            <div>
              <p>
                Eingeloggt als {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-gray-500 italic">{user?.organizationName}</p>
            </div>
            <div>
              <Button
                onClick={() => logout()}
                colorScheme="red"
                className="flex gap-2 p-2 text-white"
                variant="outline"
              >
                Abmelden
              </Button>
            </div>
          </div>

          {changedToOrganizationName && (
            <div className="mt-3 border-t border-red-200 pt-3 flex justify-between items-center gap-4">
              <p className="text-sm text-red-700">
                <strong>Achtung:</strong> Diese Seite zeigt {user?.organizationName}, du bist inzwischen aber bei{" "}
                <strong>{changedToOrganizationName}</strong> angemeldet - vermutlich durch einen anderen Tab oder eine
                parallele Claude-/MCP-Sitzung. Was du hier speicherst, landet bei {changedToOrganizationName}. Bitte
                zuerst neu laden.
              </p>
              <Button
                onClick={() => window.location.reload()}
                colorScheme="red"
                className="flex gap-2 p-2 text-white shrink-0"
              >
                Neu laden
              </Button>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default RootWrapper;
